import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  Pencil,
  FileText,
  CheckCircle2,
  Circle,
  MessageSquare,
  Send,
  GitBranch,
  Merge,
  Download,
  Image as ImageIcon,
  Share2,
  Receipt,
  ExternalLink,
  Trophy,
  CheckCheck,
  Upload,
  BadgeCheck,
  Plus,
  ChevronRight,
  Bell,
  BellOff,
  Lock,
  School,
} from "lucide-react";
import { api, BRL, formatDateBR, STATUS_LABELS, STATUS_ORDER, setNotifySchool, shownUnit } from "@/lib/api";
import useAutoRefresh from "@/lib/useAutoRefresh";
import BudgetSheet from "@/components/BudgetSheet";
import { BudgetScheduleButton } from "@/components/BudgetSchedule";
import TermsOfBudgetDialog from "@/components/TermsOfBudgetDialog";
import ItemHistoryDialog from "@/components/ItemHistoryDialog";
import CompleteItemDialog from "@/components/CompleteItemDialog";
import FdpEntriesPanel from "@/components/FdpEntriesPanel";
import BudgetItemDialog from "@/components/BudgetItemDialog";
import SchoolContactsDialog from "@/components/SchoolContactsDialog";
import BudgetAdminNoteCard from "@/components/BudgetAdminNoteCard";
import NfseDialog from "@/components/NfseDialog";
import BudgetChatPanel from "@/components/BudgetChatPanel";
import PasswordPromptDialog from "@/components/PasswordPromptDialog";
import ShareBudgetDialog from "@/components/ShareBudgetDialog";
import AutoGrowTextarea from "@/components/AutoGrowTextarea";
import GeneralPhotosDialog from "@/components/GeneralPhotosDialog";
import FinanceResultDialog from "@/components/FinanceResultDialog";
import BudgetExpensesPanel, { BUDGET_EXPENSE_STATUSES } from "@/components/BudgetExpensesPanel";
import MergeBudgetDialog, { MERGEABLE } from "@/components/MergeBudgetDialog";
import NfAntecipadaPanel, { NfaActivateButton, isNfaOpen } from "@/components/NfAntecipadaPanel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import MoneyInput from "@/components/MoneyInput";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const NFSE_PORTAL_URL = "https://www.nfse.gov.br/EmissorNacional/Login?ReturnUrl=%2femissornacional";


export default function BudgetDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [budget, setBudget] = useState(null);
  const [itemEdit, setItemEdit] = useState({ open: false, mode: "edit", item: null });
  const [schoolEditOpen, setSchoolEditOpen] = useState(false);
  const [nfseOpen, setNfseOpen] = useState(false);

  const reorderItems = async (ids) => {
    setBudget((p) => ({ ...p, items: ids.map((i) => p.items.find((x) => x.id === i)) }));
    try {
      await api.put(`/budgets/${id}/items-order`, { item_ids: ids });
      toast.success("Ordem dos itens salva");
    } catch {
      toast.error("Erro ao salvar a ordem");
      load();
    }
  };
  const [splitOpen, setSplitOpen] = useState(false);
  const [mergeOpen, setMergeOpen] = useState(false);
  const [splitSel, setSplitSel] = useState({});
  const [splitting, setSplitting] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  const [historyDialog, setHistoryDialog] = useState({ open: false, itemId: null });
  const [completeDialog, setCompleteDialog] = useState({ open: false, mode: "complete", item: null });
  // Dropdown "Outros desta escola": lista dos irmãos para troca rápida sem voltar
  const [siblings, setSiblings] = useState([]);
  const [siblingsFilter, setSiblingsFilter] = useState("all");
  const [generalPhotosOpen, setGeneralPhotosOpen] = useState(false);
  // "Avisar escola?" toggle — persisted per-budget in localStorage so a
  // closed budget stays "silent" by default on return.  Default: ON.
  const notifyKey = `amc.notifySchool.${id}`;
  const [notifySchool, setNotifySchoolState] = useState(() => {
    try { const v = localStorage.getItem(notifyKey); return v === null ? true : v === "1"; }
    catch { return true; }
  });
  useEffect(() => {
    try { localStorage.setItem(notifyKey, notifySchool ? "1" : "0"); } catch {}
    setNotifySchool(notifySchool);
    return () => setNotifySchool(true); // reset on unmount so other pages aren't affected
  }, [notifySchool, notifyKey]);
  const [msgDialog, setMsgDialog] = useState(false);
  const [msgText, setMsgText] = useState("");
  const [sendingMsg, setSendingMsg] = useState(false);

  const openItemComplete = (it) => {
    if (!it.completed) {
      // Marking a pending item as done:
      // - If budget is "iniciando": auto-save today's date, no modal, no WhatsApp dialog.
      // - If budget is "finalizado": still open the dialog so the admin can type the master password.
      if (budget?.status === "finalizado") {
        setCompleteDialog({ open: true, mode: "complete", item: it });
      } else {
        quickCompleteItem(it);
      }
      return;
    }
    // Already completed → user wants to re-open → ask confirmation + reason
    setCompleteDialog({ open: true, mode: "reopen", item: it });
  };
  const openItemDate = (it) => {
    setCompleteDialog({ open: true, mode: "change-date", item: it });
  };

  const quickCompleteItem = async (it) => {
    const today = new Date().toISOString().slice(0, 10);
    try {
      const r = await api.post(`/budgets/${id}/items/${it.id}/complete-toggle`, { completed_at: today });
      setBudget(r.data);
      toast.success("Item concluído");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao concluir");
    }
  };

  const confirmComplete = async ({ date, password }) => {
    const it = completeDialog.item;
    if (!it) return;
    const mode = completeDialog.mode;
    try {
      let r;
      if (mode === "change-date") {
        r = await api.patch(`/budgets/${id}/items/${it.id}/completed-at`, { completed_at: date, password });
      } else {
        r = await api.post(`/budgets/${id}/items/${it.id}/complete-toggle`, { completed_at: date, password });
      }
      setBudget(r.data);
      toast.success(mode === "reopen" ? "Item reaberto" : (mode === "change-date" ? "Data atualizada" : "Item concluído"));
      setCompleteDialog({ open: false, mode: "complete", item: null });
      // No WhatsApp dialog on item-level changes — school is notified by push + sininho.
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro");
    }
  };

  const load = async () => {
    const r = await api.get(`/budgets/${id}`);
    setBudget(r.data);
  };

  // Carrega orçamentos irmãos (mesma escola) para o dropdown de navegação rápida.
  useEffect(() => {
    if (!budget?.school_id) { setSiblings([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/budgets?school_id=${budget.school_id}&limit=500`);
        if (!cancelled) setSiblings((r.data || []).filter((b) => b.id !== budget.id));
      } catch { if (!cancelled) setSiblings([]); }
    })();
    return () => { cancelled = true; };
  }, [budget?.school_id, budget?.id]);

  const loadConcorrencias = async () => {
    try {
      const r = await api.get(`/budgets/${id}/concorrencias`);
      setConcorrencias(r.data || []);
    } catch {}
  };

  const generateConcorrencias = async () => {
    if (!window.confirm("Gerar 2 orçamentos concorrentes usando IA? Isso leva alguns segundos e consome créditos.")) return;
    setConcCreating(true);
    try {
      const r = await api.post(`/budgets/${id}/concorrencias`);
      toast.success(`${r.data.count} concorrências geradas`);
      await loadConcorrencias();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao gerar concorrências");
    } finally {
      setConcCreating(false);
    }
  };

  const syncConcorrencias = async () => {
    if (!window.confirm("Sincronizar os itens das concorrências com o AMC? Novos itens serão criados via IA e itens removidos do AMC também serão apagados nas concorrências.")) return;
    setConcSyncing(true);
    try {
      const r = await api.post(`/budgets/${id}/concorrencias/sync`);
      toast.success(`Sincronizado: +${r.data.added} / -${r.data.removed} itens`);
      await loadConcorrencias();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao sincronizar");
    } finally {
      setConcSyncing(false);
    }
  };

  // Load sibling navigation (for competitor pages: parent AMC + the other competitor)
  const loadNavigation = async () => {
    try {
      if (budget?.parent_budget_id) {
        const parent = await api.get(`/budgets/${budget.parent_budget_id}`);
        setParentNav({ id: parent.data.id, number: parent.data.number, label: `AMC Nº ${parent.data.number}` });
        const sibs = await api.get(`/budgets/${budget.parent_budget_id}/concorrencias`);
        setSiblingComps(sibs.data || []);
      } else if (budget?.id && !budget?.parent_budget_id) {
        setParentNav(null);
      }
    } catch {}
  };

  const openConcEdit = () => {
    const p = budget.competitor_profile || {};
    setConcEditForm({
      // profile
      legal_name: p.legal_name || "",
      cnpj: p.cnpj || "",
      address: p.address || "",
      phone: p.phone || "",
      email: p.email || "",
      representative: p.representative || "",
      payment_bank: p.payment_bank || "",
      // budget-level
      budget_date: budget.budget_date || "",
      delivery_days: budget.delivery_days || 0,
      validity_days: budget.validity_days || 15,
      warranty: budget.warranty || "",
      service_type: budget.service_type || "",
      payment_option: budget.payment_option || "",
      // items
      items: (budget.items || []).map((it) => ({
        id: it.id,
        description: it.description || "",
        quantity: it.quantity || 1,
        unit_value: it.unit_value || 0,
      })),
    });
    setConcEditOpen(true);
  };

  const saveConcEdit = async () => {
    if (!concEditForm) return;
    setConcEditSaving(true);
    try {
      // 1) Update budget-level + profile
      await api.put(`/budgets/${id}`, {
        budget_date: concEditForm.budget_date,
        delivery_days: Number(concEditForm.delivery_days) || 0,
        validity_days: Number(concEditForm.validity_days) || 0,
        warranty: concEditForm.warranty,
        service_type: concEditForm.service_type,
        payment_option: concEditForm.payment_option,
        competitor_profile: {
          legal_name: concEditForm.legal_name,
          cnpj: concEditForm.cnpj,
          address: concEditForm.address,
          phone: concEditForm.phone,
          email: concEditForm.email,
          representative: concEditForm.representative,
          payment_bank: concEditForm.payment_bank,
        },
      });
      // 2) Update each item
      for (const it of concEditForm.items) {
        await api.put(`/budgets/${id}/items/${it.id}`, {
          description: it.description,
          quantity: Number(it.quantity) || 1,
          unit_value: Number(it.unit_value) || 0,
        });
      }
      toast.success("Concorrência atualizada");
      setConcEditOpen(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao salvar");
    } finally {
      setConcEditSaving(false);
    }
  };

  useEffect(() => { load(); loadConcorrencias(); }, [id]);
  useEffect(() => { if (budget) loadNavigation(); }, [budget?.id, budget?.parent_budget_id]);

  // Live refresh every 15s while tab is visible
  const silentReload = async () => {
    try {
      const r = await api.get(`/budgets/${id}`);
      setBudget(r.data);
    } catch { /* ignore */ }
  };
  useAutoRefresh(silentReload, 15000, [id]);

  const openSplit = () => {
    setSplitSel({});
    setSplitOpen(true);
  };
  const toggleSplitItem = (iid) => setSplitSel((s) => ({ ...s, [iid]: !s[iid] }));
  const splitCount = Object.values(splitSel).filter(Boolean).length;
  const confirmSplit = async () => {
    const ids = Object.keys(splitSel).filter((k) => splitSel[k]);
    if (!ids.length) return toast.error("Selecione pelo menos 1 item");
    if (budget && ids.length === (budget.items || []).length) {
      return toast.error("Deixe pelo menos 1 item no orçamento original");
    }
    setSplitting(true);
    try {
      const r = await api.post(`/budgets/${id}/split`, { item_ids: ids });
      toast.success(`Orçamento Nº ${r.data.new_number} criado`);
      setSplitOpen(false);
      nav(`/orcamentos/${r.data.new_budget_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao desmembrar");
    } finally { setSplitting(false); }
  };

  const STATUS_FLOW = ["aguardando", "aprovado", "iniciando", "finalizado", "nota_fiscal_gerada", "nota_faturada"];
  const APPROVED_IDX = STATUS_FLOW.indexOf("aprovado");
  const [pwdDialog, setPwdDialog] = useState({ open: false, targetStatus: null });
  const [confirmDialog, setConfirmDialog] = useState({ open: false, targetStatus: null });

  // Finalize + Fiscal dialogs
  const [finalizeOpen, setFinalizeOpen] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [fiscalOpen, setFiscalOpen] = useState(false);
  const [fiscalForm, setFiscalForm] = useState({ nf_number: "", nf_iss_value: "", nf_simples_value: "", nf_file_path: "" });
  const [financeOpen, setFinanceOpen] = useState(false);
  const [fiscalSaving, setFiscalSaving] = useState(false);
  const [fiscalUploading, setFiscalUploading] = useState(false);
  const [itemsForNfOpen, setItemsForNfOpen] = useState(false);
  // Editable text in the "Itens para emissão da nota" dialog. null = use default
  const [nfItemsText, setNfItemsText] = useState(null);
  const [termCodeInput, setTermCodeInput] = useState("");
  const [savingTermCode, setSavingTermCode] = useState(false);
  const [concorrencias, setConcorrencias] = useState([]);
  const [concCreating, setConcCreating] = useState(false);
  const [concEditOpen, setConcEditOpen] = useState(false);
  const [concEditForm, setConcEditForm] = useState(null);
  const [concEditSaving, setConcEditSaving] = useState(false);
  const [concSyncing, setConcSyncing] = useState(false);
  const [parentNav, setParentNav] = useState(null);
  const [siblingComps, setSiblingComps] = useState([]);
  const [shareDialog, setShareDialog] = useState({ open: false, extraMessage: "", title: "Enviar para a escola?", headlineLabel: "Mensagem que será enviada" });

  const openShare = (opts = {}) => {
    setShareDialog({
      open: true,
      extraMessage: opts.extraMessage || "",
      title: opts.title || "Enviar para a escola?",
      headlineLabel: opts.headlineLabel || "Mensagem que será enviada",
    });
  };

  const itemsCount = budget?.items?.length || 0;
  const allItemsCompleted = itemsCount > 0 && budget.items.every((it) => it.completed);
  const canFinalize = budget?.status === "iniciando" && allItemsCompleted && !budget?.nf_antecipada;
  const locked = !!budget?.nf_antecipada;
  const canOpenFiscal = ["finalizado", "nota_fiscal_gerada", "nota_faturada"].includes(budget?.status) || isNfaOpen(budget);
  const canAnticipate = !locked && !budget?.parent_budget_id && itemsCount > 0 && ["aprovado", "iniciando"].includes(budget?.status);

  const buildWhatsAppUrl = () => {
    if (!budget) return "";
    const s = budget.school || {};
    const raw = (s.whatsapp_director || s.phone || "").replace(/\D/g, "");
    if (!raw) return "";
    const phone = raw.startsWith("55") ? raw : `55${raw}`;
    const dirName = s.director_name ? `${s.director_name}` : "Diretor(a)";
    const msg =
      `Olá ${dirName}! Concluímos os serviços referentes ao orçamento Nº *${budget.number}* ` +
      `da ${s.name || "escola"}. Podemos emitir a nota fiscal? Obrigado! — AMC Escolas`;
    return `https://wa.me/${phone}?text=${encodeURIComponent(msg)}`;
  };

  const doFinalize = async (notifyDirector) => {
    setFinalizing(true);
    try {
      await api.put(`/budgets/${id}`, { status: "finalizado", nf_director_notified: notifyDirector });
      if (notifyDirector) {
        const url = buildWhatsAppUrl();
        if (url) {
          window.open(url, "_blank", "noopener");
        } else {
          toast.info("Diretor sem WhatsApp cadastrado — status atualizado sem envio.");
        }
      }
      toast.success("Orçamento finalizado");
      setFinalizeOpen(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao finalizar");
    } finally {
      setFinalizing(false);
    }
  };

  const [taxPct, setTaxPct] = useState({ iss: 2.79, simples: 6.22 });
  const openFiscalDialog = async () => {
    let pct = taxPct;
    try {
      const r = await api.get("/finance/settings");
      pct = { iss: Number(r.data?.iss_pct ?? 2.79), simples: Number(r.data?.simples_pct ?? 6.22) };
      setTaxPct(pct);
    } catch { /* usa padrão */ }
    const total = Number(budget.total || 0);
    const simplesAuto = Number((total * pct.simples / 100).toFixed(2));
    const issAuto = Number((total * pct.iss / 100).toFixed(2));
    setFiscalForm({
      nf_number: budget.nf_number || "",
      nf_iss_value: budget.nf_iss_value ? String(budget.nf_iss_value) : String(issAuto),
      nf_simples_value: budget.nf_simples_value ? String(budget.nf_simples_value) : String(simplesAuto),
      nf_file_path: budget.nf_file_path || "",
    });
    setFiscalOpen(true);
  };

  const uploadFiscalFile = async (file) => {
    if (!file) return;
    const form = new FormData();
    form.append("file", file);
    setFiscalUploading(true);
    try {
      const r = await api.post("/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
      setFiscalForm((f) => ({ ...f, nf_file_path: r.data.path }));
      toast.success("Nota fiscal anexada");
    } catch {
      toast.error("Falha no upload");
    } finally {
      setFiscalUploading(false);
    }
  };

  const saveFiscalData = async () => {
    if (!fiscalForm.nf_number.trim()) {
      toast.error("Informe o número da nota fiscal");
      return;
    }
    setFiscalSaving(true);
    try {
      await api.put(`/budgets/${id}`, {
        nf_number: fiscalForm.nf_number.trim(),
        nf_iss_value: Number(fiscalForm.nf_iss_value) || 0,
        nf_simples_value: Number(fiscalForm.nf_simples_value) || 0,
        nf_file_path: fiscalForm.nf_file_path || "",
        ...(isNfaOpen(budget) ? {} : { status: "nota_fiscal_gerada" }),
      });
      toast.success("Dados fiscais salvos");
      setFiscalOpen(false);
      await load();
      // Open the new finance result modal automatically (cutoff 2026-10-01)
      if (!isNfaOpen(budget)) setFinanceOpen(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao salvar");
    } finally {
      setFiscalSaving(false);
    }
  };

  const changeStatus = async (s, password) => {
    try {
      const payload = { status: s };
      if (password) payload.password = password;
      await api.put(`/budgets/${id}`, payload);
      toast.success("Status atualizado");
      await load();
      setPwdDialog({ open: false, targetStatus: null });
      // Prompt admin to share status change
      const label = STATUS_LABELS[s] || s;
      openShare({
        title: "Avisar escola sobre novo status?",
        headlineLabel: "Mensagem sobre mudança de status",
        extraMessage: `Olá! O status do orçamento Nº ${budget?.number} foi atualizado para: *${label}*.\n\nConsulte os detalhes no link abaixo.`,
      });
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro");
      return false;
    }
  };

  const handleStatusChange = (s) => {
    const curIdx = STATUS_FLOW.indexOf(budget.status);
    const newIdx = STATUS_FLOW.indexOf(s);
    if (curIdx >= 0 && newIdx >= 0 && newIdx < curIdx) {
      // Backward move → ask for password (admin override)
      setPwdDialog({ open: true, targetStatus: s });
      return;
    }
    // Any status change where the CURRENT status is already approved OR later
    // asks for confirmation (no password) — these are consequential moves.
    if (curIdx >= APPROVED_IDX && s !== budget.status) {
      setConfirmDialog({ open: true, targetStatus: s });
      return;
    }
    changeStatus(s);
  };

  const downloadPdf = async () => {
    try {
      const r = await api.get(`/budgets/${id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `orcamento_${budget.number}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Falha ao gerar PDF");
    }
  };

  const downloadDeclaration = async () => {
    try {
      const r = await api.get(`/budgets/${id}/declaration-pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `declaracao_simples_${budget.number}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao gerar declaração");
    }
  };

  const downloadBundle = async () => {
    try {
      const r = await api.get(`/budgets/${id}/bundle`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `pacote_${budget.number}.zip`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Pacote ZIP baixado");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao baixar pacote");
    }
  };

  const finalizePaperwork = async () => {
    // Open the share modal RIGHT AWAY so a page re-render triggered by
    // ``load()`` (or any ZIP download side-effect) can never hide it.
    openShare({
      title: "Enviar documentação completa?",
      headlineLabel: "Mensagem para envio da documentação",
      extraMessage: budget?.boleto?.numero && budget.boleto.status !== "baixado"
        ? `Olá! Segue a documentação completa do orçamento Nº ${budget?.number} (AMC + concorrências + nota fiscal + boleto).\n\nO boleto de ${BRL(budget.boleto.valor)} vence em ${formatDateBR(budget.boleto.vencimento)} e pode ser pago também por Pix (QR Code no boleto).`
        : `Olá! Segue a documentação completa do orçamento Nº ${budget?.number} (AMC + concorrências + nota fiscal).\n\nUma pergunta: o pagamento será por *transferência* ou *boleto*?`,
    });
    try {
      await api.post(`/budgets/${id}/paperwork-done`);
      // Download the ZIP so the admin can attach it on WhatsApp
      const r = await api.get(`/budgets/${id}/bundle`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `pacote_${budget.number}.zip`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Papelada finalizada. ZIP baixado — anexe no WhatsApp.");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao finalizar papelada");
    }
  };

  /**
   * Reclique no "Papelada OK — reenviar". Diferente do primeiro uso:
   * não dispara POST (papelada já marcada), só baixa o ZIP e abre o modal
   * com uma mensagem de REENVIO pré-preenchida. Mantém a tela estável.
   */
  const resendPaperwork = async () => {
    openShare({
      title: "Reenviar documentação",
      headlineLabel: "Mensagem de reenvio",
      extraMessage: `Olá! Estou reenviando a documentação completa do orçamento Nº ${budget?.number} (AMC + concorrências + nota fiscal), caso não tenha recebido anteriormente.\n\nQualquer dúvida, estou à disposição!`,
    });
    try {
      const r = await api.get(`/budgets/${id}/bundle`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `pacote_${budget.number}.zip`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("ZIP baixado — anexe no WhatsApp.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao baixar pacote");
    }
  };

  const sharePdf = async () => {
    try {
      const r = await api.get(`/budgets/${id}/pdf`, { responseType: "blob" });
      const file = new File([r.data], `orcamento_${budget.number}.pdf`, { type: "application/pdf" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: `Orçamento ${budget.number}` });
      } else {
        downloadPdf();
      }
    } catch { toast.error("Falha ao compartilhar"); }
  };

  if (!budget) return <div className="text-slate-500">Carregando...</div>;

  const currentStatusIdx = STATUS_ORDER.indexOf(budget.status);
  const isCompetitor = !!budget.parent_budget_id;

  return (
    <div className="space-y-5 max-w-6xl">
      {/* Progress stepper — moved to the top of the page so admin sees at
          a glance where the budget stands before diving into details. */}
      {!isCompetitor && (
        <div className="bg-white rounded-xl border border-slate-100 card-shadow p-5">
          <h3 className="font-heading text-sm font-semibold text-slate-900 mb-4">Andamento do orçamento</h3>
          <div className="overflow-x-auto">
            <div className="flex items-center gap-2 min-w-max">
              {STATUS_ORDER.map((s, idx) => {
                const done = currentStatusIdx >= idx && budget.status !== "reprovado";
                const active = STATUS_ORDER[currentStatusIdx] === s;
                return (
                  <React.Fragment key={s}>
                    <div className="flex flex-col items-center text-center">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${done ? "bg-[#7c3aed] text-white" : "bg-slate-100 text-slate-400"} ${active ? "ring-4 ring-[#7c3aed]/20" : ""}`}>
                        {done ? <CheckCircle2 className="w-4 h-4" /> : idx + 1}
                      </div>
                      <p className={`text-xs mt-2 ${done ? "text-slate-900 font-medium" : "text-slate-400"}`}>{STATUS_LABELS[s]}</p>
                    </div>
                    {idx < STATUS_ORDER.length - 1 && (
                      <div className={`h-0.5 w-10 ${done && currentStatusIdx > idx ? "bg-[#7c3aed]" : "bg-slate-200"}`} />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Notify-school banner — admin can silence any change on this
          budget (uses the X-Notify-School header plumbed through axios)
          and send ad-hoc messages that always reach the client. */}
      {!isCompetitor && budget.school_id && (
        <div className="bg-white rounded-xl border border-slate-100 card-shadow px-4 py-2.5 flex items-center gap-3 flex-wrap" data-testid="notify-school-bar">
          <button
            type="button"
            onClick={() => setNotifySchoolState((v) => !v)}
            className={`flex items-center gap-2 px-2.5 py-1 rounded-full text-[11.5px] font-bold border ${
              notifySchool
                ? "bg-emerald-50 border-emerald-200 text-emerald-700"
                : "bg-slate-100 border-slate-200 text-slate-500"
            }`}
            data-testid="btn-toggle-notify"
            aria-pressed={notifySchool}
            title={notifySchool ? "A escola será avisada quando você alterar algo" : "Alterações estão silenciosas (escola não recebe push nem sininho)"}
          >
            {notifySchool ? <Bell className="w-3.5 h-3.5" /> : <BellOff className="w-3.5 h-3.5" />}
            {notifySchool ? "Avisar escola" : "Silenciado"}
          </button>
          <span className="text-[11px] text-slate-400 flex-1 min-w-0 truncate hidden sm:inline">
            {notifySchool ? "Cada alteração dispara push + sininho pro cliente." : "Nenhuma mudança nesta sessão vai pro cliente — só histórico interno."}
          </span>
          <button
            type="button"
            onClick={() => document.getElementById("budget-chat-anchor")?.scrollIntoView({ behavior: "smooth", block: "start" })}
            className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11.5px] font-bold bg-sky-50 text-sky-700 border border-sky-200 hover:bg-sky-100"
            data-testid="btn-scroll-chat"
          >
            <MessageSquare className="w-3.5 h-3.5" />
            Conversa com a escola
          </button>
        </div>
      )}

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        <Button variant="ghost" size="icon" onClick={() => nav("/orcamentos")} data-testid="btn-back-detail">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <Link to="/orcamentos" className="hover:underline">Orçamentos</Link>
            <span>/</span>
            <span className="truncate">Nº {budget.number}</span>
          </div>
          <h1 className="font-heading text-xl md:text-2xl font-bold text-slate-900">
            Orçamento Nº {budget.number}
          </h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="min-w-[180px]">
            <Select value={budget.status} onValueChange={handleStatusChange}>
              <SelectTrigger data-testid="select-change-status" className="h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(STATUS_LABELS).map(([v, l]) => (
                  <SelectItem key={v} value={v}>{l}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {siblings.length > 0 && (
            <div className="min-w-[220px] flex items-center gap-1">
              <Select
                value=""
                onValueChange={(v) => { if (v) nav(`/orcamentos/${v}`); }}
              >
                <SelectTrigger data-testid="select-sibling-budget" className="h-9">
                  <SelectValue placeholder={`Outro orç. da escola (${siblings.filter((b) => siblingsFilter === "all" || b.status === siblingsFilter).length})`} />
                </SelectTrigger>
                <SelectContent className="max-h-[360px]">
                  <div className="p-2 border-b sticky top-0 bg-white z-10">
                    <Select value={siblingsFilter} onValueChange={setSiblingsFilter}>
                      <SelectTrigger className="h-8 text-xs" data-testid="select-sibling-filter"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos ({siblings.length})</SelectItem>
                        {Object.entries(STATUS_LABELS).map(([v, l]) => {
                          const n = siblings.filter((b) => b.status === v).length;
                          if (!n) return null;
                          return <SelectItem key={v} value={v}>{l} ({n})</SelectItem>;
                        })}
                      </SelectContent>
                    </Select>
                  </div>
                  {siblings
                    .filter((b) => siblingsFilter === "all" || b.status === siblingsFilter)
                    .sort((a, b) => (Number(b.number) || 0) - (Number(a.number) || 0))
                    .map((b) => (
                      <SelectItem key={b.id} value={b.id} data-testid={`opt-sibling-${b.number}`}>
                        <span className="truncate">
                          Nº {b.number} · {STATUS_LABELS[b.status] || b.status}
                          {b.faz_depois_paga ? " · FDP" : ""} · {BRL(b.total || 0)}
                        </span>
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {!["nota_fiscal_gerada", "nota_faturada"].includes(budget?.status) && (
            <Button
              variant="outline"
              size="sm"
              onClick={openSplit}
              data-testid="btn-split-budget"
              disabled={locked || !budget?.items?.length || budget.items.length < 2}
              className="border-amber-200 text-amber-700 hover:bg-amber-50"
            >
              <GitBranch className="w-4 h-4 mr-1.5" />
              Desmembrar
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setMergeOpen(true)}
            data-testid="btn-merge-budget"
            disabled={locked || !budget?.items?.length || !MERGEABLE.includes(budget.status)}
            title={MERGEABLE.includes(budget?.status) ? "" : "Só para orçamentos Aguardando, Aprovado ou Iniciado"}
            className="border-sky-200 text-sky-700 hover:bg-sky-50"
          >
            <Merge className="w-4 h-4 mr-1.5" />
            Unir orçamento
          </Button>
          <Button variant="outline" size="sm" onClick={downloadPdf} data-testid="btn-download-pdf">
            <Download className="w-4 h-4 mr-1.5" />
            PDF
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setGeneralPhotosOpen(true)}
            data-testid="btn-general-photos"
            className="border-violet-200 text-[#7c3aed] hover:bg-violet-50"
          >
            <ImageIcon className="w-4 h-4 mr-1.5" />
            Fotos{(budget.general_photos?.length || 0) > 0 ? ` (${budget.general_photos.length})` : ""}
          </Button>
          {/* Enviar/Reenviar WhatsApp — usado no INÍCIO do orçamento.
              Depois que a nota fiscal é gerada, o fluxo é via "Opções da nota →
              Papelada OK — reenviar" (lá embaixo), então escondemos este
              atalho para evitar duplicidade. */}
          {!["nota_fiscal_gerada", "nota_faturada"].includes(budget.status) && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => openShare()}
              data-testid="btn-reshare-whatsapp"
              className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
            >
              <MessageSquare className="w-4 h-4 mr-1.5" />
              Enviar/Reenviar
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={sharePdf} data-testid="btn-share-pdf" className="hidden sm:inline-flex">
            <Share2 className="w-4 h-4 mr-1.5" />
            Compartilhar
          </Button>
          {budget.status === "nota_fiscal_gerada" && (
            <Button
              size="sm"
              onClick={async () => {
                if (!window.confirm(`Dar baixa no orçamento Nº ${budget.number} como PAGO?`)) return;
                try {
                  await api.put(`/budgets/${id}`, { status: "nota_faturada" });
                  toast.success("Marcado como pago");
                  await load();
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Erro");
                }
              }}
              data-testid="btn-mark-paid"
              className="bg-violet-600 hover:bg-violet-700 text-white"
            >
              <BadgeCheck className="w-4 h-4 mr-1.5" />
              Marcar como pago
            </Button>
          )}
          {(budget.status === "validade_expirada" || budget.status === "reprovado") && (
            <Button
              size="sm"
              onClick={async () => {
                const days = window.prompt(
                  `Reabrir orçamento Nº ${budget.number} com reavaliação.\nQuantos dias de validade a partir de hoje?`,
                  "15",
                );
                if (!days) return;
                const n = parseInt(days, 10);
                if (!n || n < 1 || n > 180) {
                  toast.error("Informe um número entre 1 e 180");
                  return;
                }
                try {
                  await api.post(`/budgets/${id}/reopen`, { validity_days: n });
                  toast.success(`Orçamento reaberto por ${n} dias`);
                  await load();
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Erro ao reabrir");
                }
              }}
              data-testid="btn-reopen-budget"
              className="bg-orange-600 hover:bg-orange-700 text-white"
            >
              <GitBranch className="w-4 h-4 mr-1.5" />
              Reabrir
            </Button>
          )}
          {budget.school?.id && (
            <Button size="sm" variant="outline" onClick={() => setSchoolEditOpen(true)} className="border-[#1e3a8a]/30 text-[#1e3a8a]" data-testid="btn-edit-school-contacts">
              <School className="w-4 h-4 mr-1.5" />
              Escola
            </Button>
          )}
          <SchoolContactsDialog open={schoolEditOpen} onOpenChange={setSchoolEditOpen} school={budget.school} onSaved={load} />
          <NfseDialog open={nfseOpen} onOpenChange={setNfseOpen} budgetId={budget.id} onEditSchool={() => setSchoolEditOpen(true)} onEmitted={(r) => { load(); if (r?.chave && !isNfaOpen(budget)) { setNfseOpen(false); setFinanceOpen(true); } }} />
          {locked ? (
            <span className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md bg-indigo-50 border border-indigo-200 text-indigo-700 text-xs font-semibold" data-testid="badge-nfa-locked">
              <Lock className="w-3.5 h-3.5" />
              Travado (nota antecipada)
            </span>
          ) : (
            <Link to={`/orcamentos/${id}/editar`}>
              <Button size="sm" className="bg-[#7c3aed] hover:bg-[#6d28d9] text-white" data-testid="btn-edit-budget">
                <Pencil className="w-4 h-4 mr-1.5" />
                Editar
              </Button>
            </Link>
          )}
        </div>
      </div>

      <NfAntecipadaPanel budget={budget} onChange={load} onOpenFiscal={openFiscalDialog} />

      {/* The sheet */}
      {!isCompetitor && (
        <BudgetAdminNoteCard budgetId={budget.id} note={budget.admin_note} onSaved={(t) => setBudget((p) => ({ ...p, admin_note: t }))} />
      )}
      <BudgetSheet
        budget={budget}
        onTermClick={() => setTermsOpen(true)}
        onItemHistoryClick={(iid) => setHistoryDialog({ open: true, itemId: iid })}
        onItemCompleteClick={openItemComplete}
        onItemDateClick={openItemDate}
        onItemPhotoUpdate={(itemId, updated) => {
          setBudget((prev) => prev && ({
            ...prev,
            items: (prev.items || []).map((x) => (x.id === itemId ? { ...x, ...updated } : x)),
          }));
        }}
        onItemEdit={!locked && !isCompetitor ? (it) => setItemEdit({ open: true, mode: "edit", item: it }) : undefined}
        showAdminNotes
        onReorder={!isCompetitor && (budget?.items?.length || 0) > 1 ? reorderItems : undefined}
        onItemAdd={!locked && !isCompetitor && !(budget?.faz_depois_paga && budget?.fdp_log_mode) ? () => setItemEdit({ open: true, mode: "add", item: null }) : undefined}
        canManageItems
      />
      <BudgetItemDialog
        open={itemEdit.open}
        mode={itemEdit.mode}
        initialItem={itemEdit.item}
        budget={budget}
        schoolId={budget?.school_id}
        schoolName={budget?.school?.name || budget?.school_name}
        allowDelete
        onClose={() => setItemEdit((d) => ({ ...d, open: false }))}
        onSaved={() => { setItemEdit((d) => ({ ...d, open: false })); load(); }}
        onDeleted={() => { setItemEdit((d) => ({ ...d, open: false })); load(); }}
      />

      {/* Competitor banner + edit button */}
      {isCompetitor && (
        <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-4 flex items-center justify-between gap-3 flex-wrap" data-testid="competitor-banner">
          <div className="flex items-center gap-3 min-w-0">
            <Trophy className="w-5 h-5 text-amber-600 shrink-0" />
            <div className="min-w-0">
              <p className="font-heading text-sm font-bold text-amber-900 truncate">
                Concorrência: {budget.competitor_name || "—"}
              </p>
              <p className="text-[11px] text-amber-700">
                Vinculada ao orçamento AMC original.{" "}
                {budget.parent_budget_id && (
                  <Link to={`/orcamentos/${budget.parent_budget_id}`} className="font-semibold hover:underline" data-testid="link-back-to-parent">
                    Voltar ao orçamento AMC →
                  </Link>
                )}
              </p>
            </div>
          </div>
          <Button
            onClick={openConcEdit}
            className="bg-amber-600 hover:bg-amber-700 text-white"
            data-testid="btn-edit-competitor"
          >
            <Pencil className="w-4 h-4 mr-2" />
            Editar concorrência
          </Button>
        </div>
      )}

      {/* Navigation chips: switch between AMC original + its 2 competitors without leaving the view */}
      {(parentNav || (concorrencias.length > 0 && !isCompetitor)) && (
        <div className="bg-slate-50 rounded-xl border border-slate-200 p-3 flex items-center gap-2 flex-wrap" data-testid="budget-navigation">
          <span className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mr-1">Trocar:</span>
          {isCompetitor && parentNav ? (
            <>
              <Link
                to={`/orcamentos/${parentNav.id}`}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-full border border-[#7c3aed] text-[#7c3aed] bg-white hover:bg-violet-50"
                data-testid="nav-chip-amc"
              >
                AMC Nº {parentNav.number}
              </Link>
              {siblingComps.map((c) => (
                <Link
                  key={c.id}
                  to={`/orcamentos/${c.id}`}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-full ${c.id === budget.id ? "bg-amber-600 text-white border border-amber-600" : "border border-amber-300 text-amber-700 bg-white hover:bg-amber-50"}`}
                  data-testid={`nav-chip-${c.competitor_name?.toLowerCase().split(' ')[0]}`}
                >
                  {c.competitor_name} Nº {c.number}
                </Link>
              ))}
            </>
          ) : (
            <>
              <span
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-full bg-[#7c3aed] text-white"
                data-testid="nav-chip-amc-current"
              >
                AMC Nº {budget.number}
              </span>
              {concorrencias.map((c) => (
                <Link
                  key={c.id}
                  to={`/orcamentos/${c.id}`}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-full border border-amber-300 text-amber-700 bg-white hover:bg-amber-50"
                  data-testid={`nav-chip-${c.competitor_name?.toLowerCase().split(' ')[0]}`}
                >
                  {c.competitor_name} Nº {c.number}
                </Link>
              ))}
            </>
          )}
        </div>
      )}

      {/* FDP Entries Panel — somente para orçamentos NOVOS no formato de lançamentos */}
      {budget?.faz_depois_paga && budget?.fdp_log_mode && (
        <FdpEntriesPanel
          budget={budget}
          fdpMax={budget?.school?.fdp_max_value || 0}
          onChange={load}
        />
      )}

      {/* Botão para ver detalhamento dos lançamentos (mesmo após consolidação) */}
      {budget?.faz_depois_paga && !budget?.fdp_log_mode && budget?.fdp_consolidated_at && (
        <Link
          to={`/orcamentos/${budget.id}/lancamentos`}
          className="block bg-gradient-to-br from-indigo-500 to-violet-600 rounded-2xl p-4 text-white hover:shadow-lg transition-shadow"
          data-testid="btn-view-fdp-details"
        >
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-white/80">Prestação de contas FDP</p>
              <p className="text-sm font-black">Ver serviços executados (1 a 1)</p>
              <p className="text-xs text-white/80 mt-0.5">Lista cronológica + fotos antes/depois + PDF</p>
            </div>
            <ChevronRight className="w-6 h-6 shrink-0" />
          </div>
        </Link>
      )}

      {/* Action panel: Finalize + Fiscal + Concorrência */}
      {(canFinalize || canOpenFiscal || itemsCount > 0) && (
        <div className="bg-white rounded-xl border border-slate-100 card-shadow p-5" data-testid="budget-action-panel">
          <h3 className="font-heading text-sm font-semibold text-slate-900 mb-3">Próximos passos</h3>
          <div className="flex flex-wrap gap-2">
            {canFinalize && (
              <Button
                onClick={() => setFinalizeOpen(true)}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="btn-finalize-budget"
              >
                <CheckCheck className="w-4 h-4 mr-2" />
                Finalizar orçamento
              </Button>
            )}
            {canAnticipate && <NfaActivateButton budget={budget} onChange={load} />}
            {/* Concorrência: always available after items exist (if no concorrencias yet and not a competitor itself) */}
            {itemsCount > 0 && !budget.parent_budget_id && (
              concorrencias.length === 0 ? (
                <Button
                  onClick={generateConcorrencias}
                  disabled={concCreating}
                  variant="outline"
                  className="border-amber-300 text-amber-700 hover:bg-amber-50"
                  data-testid="btn-create-concorrencia"
                >
                  <Trophy className="w-4 h-4 mr-2" />
                  {concCreating ? "Gerando com IA..." : "Criar concorrência"}
                </Button>
              ) : (
                <Button
                  onClick={syncConcorrencias}
                  disabled={concSyncing}
                  variant="outline"
                  className="border-amber-300 text-amber-700 hover:bg-amber-50"
                  data-testid="btn-sync-concorrencias"
                >
                  <GitBranch className="w-4 h-4 mr-2" />
                  {concSyncing ? "Sincronizando..." : "Sincronizar concorrências"}
                </Button>
              )
            )}
            {!isCompetitor && <BudgetScheduleButton budget={budget} onSaved={load} />}
            {canOpenFiscal && !budget.nf_number && (
              <div className="w-full space-y-2">
                <Button
                  onClick={() => setNfseOpen(true)}
                  className="bg-[#7c3aed] hover:bg-[#6d28d9] text-white"
                  data-testid="btn-open-nfse"
                >
                  <Receipt className="w-4 h-4 mr-2" />
                  Emitir nota fiscal
                </Button>
                <p className="text-[11px] text-slate-400 flex flex-wrap gap-x-3 gap-y-1" data-testid="nf-old-flow-links">
                  <span>Modo antigo (reserva):</span>
                  <button type="button" onClick={() => setItemsForNfOpen(true)} className="underline hover:text-slate-600" data-testid="btn-items-for-nf">Ver itens</button>
                  <a href={NFSE_PORTAL_URL} target="_blank" rel="noreferrer" className="underline hover:text-slate-600" data-testid="btn-portal-nfe-sp">Emitir no portal</a>
                  <button type="button" onClick={openFiscalDialog} className="underline hover:text-slate-600" data-testid="btn-gravar-nf">Gravar dados da nota</button>
                </p>
              </div>
            )}
            {canOpenFiscal && budget.nf_number && (() => {
              const hasFile = !!budget.nf_file_path;
              const hasComps = concorrencias.length >= 2;
              const bundleReady = hasFile && hasComps;
              const paperworkDone = !!budget.papelada_ok;
              return (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      className="bg-[#7c3aed] hover:bg-[#6d28d9] text-white"
                      data-testid="btn-fiscal-options"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Opções da nota
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-64">
                    <DropdownMenuItem onClick={() => setNfseOpen(true)} data-testid="opt-open-nfse">
                      <Receipt className="w-4 h-4 mr-2 text-emerald-600" />
                      {budget.nfse?.numero ? `NFS-e Nº ${budget.nfse.numero}${budget.boleto?.numero ? " e boleto" : ""}` : "Emitir nota fiscal (online)"}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => setItemsForNfOpen(true)} data-testid="opt-items-for-nf">
                      <FileText className="w-4 h-4 mr-2 text-[#7c3aed]" />
                      Ver itens para emissão
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <a
                        href={NFSE_PORTAL_URL}
                        target="_blank"
                        rel="noreferrer"
                        data-testid="opt-portal-nfe-sp"
                      >
                        <ExternalLink className="w-4 h-4 mr-2 text-sky-600" />
                        Emitir no portal (Nacional)
                      </a>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={openFiscalDialog} data-testid="opt-edit-nf">
                      <Receipt className="w-4 h-4 mr-2 text-[#7c3aed]" />
                      Editar dados da nota
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={downloadDeclaration} data-testid="opt-download-declaration">
                      <FileText className="w-4 h-4 mr-2 text-emerald-600" />
                      Carta do Simples
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    {bundleReady ? (
                      <DropdownMenuItem onClick={downloadBundle} data-testid="opt-download-bundle">
                        <Download className="w-4 h-4 mr-2 text-slate-700" />
                        Baixar pacote (ZIP)
                      </DropdownMenuItem>
                    ) : (
                      <DropdownMenuItem disabled data-testid="opt-bundle-locked">
                        <Download className="w-4 h-4 mr-2 text-slate-400" />
                        <span className="text-slate-400">
                          Pacote (ZIP) — {!hasComps ? "falta concorrência" : "falta PDF"}
                        </span>
                      </DropdownMenuItem>
                    )}
                    {bundleReady && !paperworkDone && (
                      <DropdownMenuItem onClick={finalizePaperwork} data-testid="opt-finalize-paperwork">
                        <CheckCircle2 className="w-4 h-4 mr-2 text-emerald-600" />
                        Documentação feita e enviada
                      </DropdownMenuItem>
                    )}
                    {paperworkDone && (
                      <DropdownMenuItem
                        onClick={resendPaperwork}
                        data-testid="opt-resend-paperwork"
                        title={budget.papelada_at ? `Finalizada em ${new Date(budget.papelada_at).toLocaleString("pt-BR")} — clique para reenviar` : "Clique para reenviar"}
                      >
                        <CheckCircle2 className="w-4 h-4 mr-2 text-emerald-700" />
                        Papelada OK — reenviar
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              );
            })()}
          </div>
          {canFinalize && (
            <p className="text-[11px] text-slate-500 mt-2">
              Todos os {itemsCount} itens estão concluídos. Finalize para liberar a emissão da nota fiscal.
            </p>
          )}

          {/* Concorrências list */}
          {concorrencias.length > 0 && (
            <div className="mt-4 pt-4 border-t border-slate-100" data-testid="concorrencias-list">
              <h4 className="text-xs font-semibold text-slate-700 mb-2 flex items-center gap-2">
                <Trophy className="w-3.5 h-3.5 text-amber-600" />
                Orçamentos concorrentes
              </h4>
              <ul className="space-y-2">
                {concorrencias.map((c) => (
                  <li key={c.id} className="flex items-center justify-between gap-3 bg-amber-50/60 border border-amber-100 rounded-lg px-3 py-2">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-slate-900 truncate">{c.competitor_name}</p>
                      <p className="text-[11px] text-slate-500">Nº {c.number} · {c.items?.length || 0} itens · <strong className="text-slate-700">{BRL(c.total)}</strong></p>
                    </div>
                    <Link
                      to={`/orcamentos/${c.id}`}
                      className="text-[11px] font-semibold text-[#7c3aed] hover:underline shrink-0"
                      data-testid={`link-concorrencia-${c.id}`}
                    >
                      Abrir →
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Progress stepper moved to the top of the page — see below just under the opening wrapper */}

      {!isCompetitor && <BudgetExpensesPanel budget={budget} canAdd={BUDGET_EXPENSE_STATUSES.includes(budget.status)} onOpenResult={() => setFinanceOpen(true)} />}

      {/* History */}
      {budget.history?.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-100 card-shadow p-5">
          <h3 className="font-heading text-sm font-semibold text-slate-900 mb-3">Histórico</h3>
          <ul className="space-y-2">
            {budget.history.slice(0, 10).map((h) => (
              <li key={h.id} className="text-xs flex items-start gap-2">
                <Circle className="w-2 h-2 text-[#7c3aed] mt-1 shrink-0 fill-current" />
                <div>
                  <span className="text-slate-700">{h.detail}</span>
                  <span className="text-slate-400 ml-2">— {h.user_name} • {new Date(h.created_at).toLocaleString("pt-BR")}</span>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      <TermsOfBudgetDialog open={termsOpen} onOpenChange={setTermsOpen} text={budget.awareness_term} />

      <ItemHistoryDialog
        open={historyDialog.open}
        onOpenChange={(o) => setHistoryDialog((d) => ({ ...d, open: o }))}
        budgetId={id}
        itemId={historyDialog.itemId}
      />

      <CompleteItemDialog
        open={completeDialog.open}
        onOpenChange={(o) => setCompleteDialog((d) => ({ ...d, open: o }))}
        mode={completeDialog.mode}
        requirePassword={budget.status === "finalizado"}
        initialDate={completeDialog.item?.completed_at || ""}
        onConfirm={confirmComplete}
      />

      <PasswordPromptDialog
        open={pwdDialog.open}
        onOpenChange={(o) => setPwdDialog((p) => ({ ...p, open: o }))}
        title="Retroceder status"
        description={`Para voltar o status de "${STATUS_LABELS[budget.status] || budget.status}" para "${STATUS_LABELS[pwdDialog.targetStatus] || pwdDialog.targetStatus}", informe a senha de administrador.`}
        onConfirm={(pwd) => changeStatus(pwdDialog.targetStatus, pwd)}
      />

      <AlertDialog open={confirmDialog.open} onOpenChange={(o) => setConfirmDialog((d) => ({ ...d, open: o }))}>
        <AlertDialogContent data-testid="confirm-status-change-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar alteração de status</AlertDialogTitle>
            <AlertDialogDescription>
              Este orçamento já está em{" "}
              <strong>{STATUS_LABELS[budget.status] || budget.status}</strong>. Você
              realmente quer alterá-lo para{" "}
              <strong>{STATUS_LABELS[confirmDialog.targetStatus] || confirmDialog.targetStatus}</strong>?
              A escola será notificada automaticamente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="btn-cancel-status-change">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                const target = confirmDialog.targetStatus;
                setConfirmDialog({ open: false, targetStatus: null });
                if (target) changeStatus(target);
              }}
              data-testid="btn-confirm-status-change"
              className="bg-amber-600 hover:bg-amber-700 text-white"
            >
              Sim, alterar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Competitor edit dialog */}
      <Dialog open={concEditOpen} onOpenChange={setConcEditOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="competitor-edit-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Trophy className="w-5 h-5 text-amber-600" />
              Editar concorrência — {budget.competitor_name}
            </DialogTitle>
            <DialogDescription>
              Edite os dados da empresa, do orçamento e dos itens. Clique em "Salvar tudo" ao final.
            </DialogDescription>
          </DialogHeader>

          {concEditForm && (
            <div className="space-y-4">
              {/* Profile */}
              <div className="border border-slate-200 rounded-xl p-3 space-y-3">
                <h4 className="text-xs font-bold text-slate-700 uppercase tracking-wider">Perfil da empresa</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div><Label className="text-xs">Razão social</Label>
                    <Input value={concEditForm.legal_name} onChange={(e)=>setConcEditForm(f=>({...f,legal_name:e.target.value}))} data-testid="cc-legal_name" /></div>
                  <div><Label className="text-xs">CNPJ</Label>
                    <Input value={concEditForm.cnpj} onChange={(e)=>setConcEditForm(f=>({...f,cnpj:e.target.value}))} data-testid="cc-cnpj" /></div>
                  <div className="md:col-span-2"><Label className="text-xs">Endereço</Label>
                    <Input value={concEditForm.address} onChange={(e)=>setConcEditForm(f=>({...f,address:e.target.value}))} data-testid="cc-address" /></div>
                  <div><Label className="text-xs">Telefone / WhatsApp</Label>
                    <Input value={concEditForm.phone} onChange={(e)=>setConcEditForm(f=>({...f,phone:e.target.value}))} data-testid="cc-phone" /></div>
                  <div><Label className="text-xs">E-mail</Label>
                    <Input value={concEditForm.email} onChange={(e)=>setConcEditForm(f=>({...f,email:e.target.value}))} data-testid="cc-email" /></div>
                  <div><Label className="text-xs">Representante</Label>
                    <Input value={concEditForm.representative} onChange={(e)=>setConcEditForm(f=>({...f,representative:e.target.value}))} data-testid="cc-representative" /></div>
                  <div><Label className="text-xs">Dados bancários</Label>
                    <Input value={concEditForm.payment_bank} onChange={(e)=>setConcEditForm(f=>({...f,payment_bank:e.target.value}))} data-testid="cc-bank" /></div>
                </div>
              </div>

              {/* Budget fields */}
              <div className="border border-slate-200 rounded-xl p-3 space-y-3">
                <h4 className="text-xs font-bold text-slate-700 uppercase tracking-wider">Dados do orçamento</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div><Label className="text-xs">Data</Label>
                    <Input type="date" value={concEditForm.budget_date} onChange={(e)=>setConcEditForm(f=>({...f,budget_date:e.target.value}))} data-testid="cc-date" /></div>
                  <div><Label className="text-xs">Prazo entrega (dias)</Label>
                    <Input type="number" value={concEditForm.delivery_days} onChange={(e)=>setConcEditForm(f=>({...f,delivery_days:e.target.value}))} data-testid="cc-delivery" /></div>
                  <div><Label className="text-xs">Validade (dias)</Label>
                    <Input type="number" value={concEditForm.validity_days} onChange={(e)=>setConcEditForm(f=>({...f,validity_days:e.target.value}))} data-testid="cc-validity" /></div>
                  <div><Label className="text-xs">Garantia</Label>
                    <Input value={concEditForm.warranty} onChange={(e)=>setConcEditForm(f=>({...f,warranty:e.target.value}))} data-testid="cc-warranty" /></div>
                  <div className="col-span-2 md:col-span-2"><Label className="text-xs">Tipo de serviço (ref.)</Label>
                    <Input value={concEditForm.service_type} onChange={(e)=>setConcEditForm(f=>({...f,service_type:e.target.value}))} data-testid="cc-service" /></div>
                  <div className="col-span-2 md:col-span-2"><Label className="text-xs">Forma de pagamento</Label>
                    <Input value={concEditForm.payment_option} onChange={(e)=>setConcEditForm(f=>({...f,payment_option:e.target.value}))} data-testid="cc-payment" /></div>
                </div>
              </div>

              {/* Items */}
              <div className="border border-slate-200 rounded-xl p-3 space-y-3">
                <h4 className="text-xs font-bold text-slate-700 uppercase tracking-wider">Itens ({concEditForm.items.length})</h4>
                <div className="space-y-2 max-h-80 overflow-y-auto">
                  {concEditForm.items.map((it, idx) => (
                    <div key={it.id} className="bg-slate-50 border border-slate-100 rounded-lg p-2 space-y-2" data-testid={`cc-item-${idx}`}>
                      <AutoGrowTextarea
                        value={it.description}
                        onChange={(e)=>setConcEditForm(f=>({...f, items: f.items.map((x,i)=> i===idx?{...x, description:e.target.value}:x)}))}
                        minRows={2}
                        maxRows={8}
                        className="text-sm"
                        placeholder={`Descrição do item ${idx+1}`}
                      />
                      <div className="grid grid-cols-2 gap-2">
                        <div><Label className="text-[10px]">Qtd</Label>
                          <Input type="number" step="0.01" value={it.quantity}
                            onChange={(e)=>setConcEditForm(f=>({...f, items: f.items.map((x,i)=> i===idx?{...x, quantity:e.target.value}:x)}))} /></div>
                        <div><Label className="text-[10px]">Valor unitário (R$)</Label>
                          <MoneyInput value={it.unit_value}
                            onChange={(v)=>setConcEditForm(f=>({...f, items: f.items.map((x,i)=> i===idx?{...x, unit_value:v}:x)}))} /></div>
                      </div>
                      <p className="text-[11px] text-slate-500">Total: <strong>{BRL((Number(it.quantity)||0) * (Number(it.unit_value)||0))}</strong></p>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-slate-500">
                  Total atual: <strong className="text-slate-900">{BRL(concEditForm.items.reduce((s, it) => s + (Number(it.quantity)||0) * (Number(it.unit_value)||0), 0))}</strong>
                </p>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setConcEditOpen(false)} disabled={concEditSaving} data-testid="btn-cc-cancel">
              Cancelar
            </Button>
            <Button
              onClick={saveConcEdit}
              disabled={concEditSaving}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="btn-cc-save"
            >
              {concEditSaving ? "Salvando..." : "Salvar tudo"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Items for NF emission dialog */}
      {(() => {
        const _stripBB = (s) => (s || "").replace(/\[\/?b\]/gi, "").replace(/\[\/?i\]/gi, "");
        const defaultItemsText = (budget.items || [])
          .map((it) => {
            const desc = _stripBB(it.description || "(sem descrição)").trim();
            const brl = (v) => Number(v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
            const qty = Number(it.quantity || 1);
            // Item that already lists its own values (two services in one) — don't append the total.
            if (/R\$\s*\d/.test(desc)) return desc;
            if (qty !== 1) {
              const unit = shownUnit(it) || Number(it.total || 0) / qty;
              return `${desc} . ${qty.toLocaleString("pt-BR")} x ${brl(unit)} = ${brl(it.total)}`;
            }
            return `${desc} . ${brl(it.total)}`;
          })
          .join("\n");
        const school = budget.school || {};
        const collabCode = (school.current_term_code || school.new_term_code || "").trim();
        const collabFull = collabCode
          ? `Despesa custeada com recursos do termo de colaboração n, ${collabCode} - SE firmada com o município de São Bernardo do campo`
          : "";
        const pdde = _stripBB(school.pdde_term || "Despesa custeada com recursos do PDDE básico").trim();

        const copyText = async (label, text) => {
          try {
            await navigator.clipboard.writeText(text);
            toast.success(`${label} copiado`);
          } catch {
            toast.error("Falha ao copiar — selecione manualmente");
          }
        };

        const appendToItems = (termLabel, termText) => {
          if (!termText) return;
          setNfItemsText((prev) => {
            const base = (prev ?? defaultItemsText).trimEnd();
            // Avoid double-appending the same termo
            if (base.includes(termText)) {
              toast.info(`${termLabel} já está no texto`);
              return prev ?? defaultItemsText;
            }
            toast.success(`${termLabel} adicionado abaixo dos itens`);
            return `${base}\n\n${termText}`;
          });
        };

        const resetItems = () => {
          setNfItemsText(defaultItemsText);
          toast.success("Itens restaurados");
        };

        const currentItemsText = nfItemsText ?? defaultItemsText;

        const saveTermCode = async () => {
          const code = (termCodeInput || "").trim();
          if (!code) { toast.error("Informe o número do termo"); return; }
          setSavingTermCode(true);
          try {
            await api.patch(`/schools/${school.id}`, { code });
            toast.success("Número do termo salvo");
            setTermCodeInput("");
            await load();
          } catch (e) {
            toast.error(e?.response?.data?.detail || "Erro ao salvar");
          } finally {
            setSavingTermCode(false);
          }
        };

        return (
          <Dialog open={itemsForNfOpen} onOpenChange={(o) => { setItemsForNfOpen(o); if (!o) setNfItemsText(null); }}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="items-for-nf-dialog">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-[#7c3aed]" />
                  Itens para emissão da nota
                </DialogTitle>
                <DialogDescription>
                  Copie os blocos abaixo e cole no site da Receita Federal ao emitir a nota.
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4">
                {/* Items — EDITÁVEL */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <Label className="text-xs font-bold text-slate-700">Itens ({budget.items?.length || 0})</Label>
                    <div className="flex gap-1.5">
                      <Button size="sm" variant="ghost" onClick={resetItems} className="h-7 text-[11px] text-slate-500" data-testid="btn-reset-items">
                        Restaurar
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => copyText("Itens", currentItemsText)} data-testid="btn-copy-items">
                        <FileText className="w-3.5 h-3.5 mr-1" /> Copiar
                      </Button>
                    </div>
                  </div>
                  <Textarea
                    value={currentItemsText}
                    onChange={(e) => setNfItemsText(e.target.value)}
                    rows={10}
                    className="font-mono text-xs"
                    data-testid="items-for-nf-text"
                    placeholder="Edite os itens aqui — adicione o termo embaixo para copiar tudo de uma vez"
                  />
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <p className="text-[11px] text-slate-500">Total: <strong>{BRL(budget.total)}</strong></p>
                    <div className="flex gap-1.5">
                      {collabFull && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => appendToItems("Termo de colaboração", collabFull)}
                          className="h-7 text-[10px] border-amber-300 text-amber-700 hover:bg-amber-50"
                          data-testid="btn-append-collab"
                        >
                          <Plus className="w-3 h-3 mr-1" /> Colaboração abaixo
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => appendToItems("Termo PDDE", pdde)}
                        className="h-7 text-[10px] border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                        data-testid="btn-append-pdde"
                      >
                        <Plus className="w-3 h-3 mr-1" /> PDDE abaixo
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Termo de colaboração */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs font-bold text-slate-700">
                      Termo de colaboração {collabCode && <span className="text-slate-500">— nº {collabCode}</span>}
                    </Label>
                    {collabFull && (
                      <Button size="sm" variant="outline" onClick={() => copyText("Termo de colaboração", collabFull)} data-testid="btn-copy-collab">
                        <FileText className="w-3.5 h-3.5 mr-1" /> Copiar
                      </Button>
                    )}
                  </div>
                  {collabFull ? (
                    <Textarea value={collabFull} readOnly rows={3} className="text-xs" data-testid="collab-term-text" />
                  ) : (
                    <div className="bg-amber-50 border-2 border-amber-200 rounded-lg p-3 space-y-2" data-testid="collab-term-missing-warning">
                      <p className="text-xs text-amber-800 font-semibold flex items-start gap-1.5">
                        <span className="text-lg leading-none">⚠</span>
                        <span>Esta escola ainda não tem o número do termo de colaboração cadastrado. Informe abaixo (ex: 102/2022):</span>
                      </p>
                      <div className="flex gap-2">
                        <Input
                          placeholder="102/2022"
                          value={termCodeInput}
                          onChange={(e) => setTermCodeInput(e.target.value)}
                          data-testid="input-term-code"
                          className="flex-1"
                        />
                        <Button
                          onClick={saveTermCode}
                          disabled={savingTermCode || !termCodeInput.trim()}
                          className="bg-amber-600 hover:bg-amber-700 text-white"
                          data-testid="btn-save-term-code"
                        >
                          {savingTermCode ? "Salvando..." : "Salvar"}
                        </Button>
                      </div>
                      <p className="text-[10px] text-amber-700">
                        Pré-visualização: <em>Despesa custeada com recursos do termo de colaboração n, <strong>{termCodeInput || "___"}</strong> - SE firmada com o município de São Bernardo do campo</em>
                      </p>
                    </div>
                  )}
                </div>

                {/* Termo PDDE */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs font-bold text-slate-700">Termo PDDE</Label>
                    <Button size="sm" variant="outline" onClick={() => copyText("Termo PDDE", pdde)} data-testid="btn-copy-pdde">
                      <FileText className="w-3.5 h-3.5 mr-1" /> Copiar
                    </Button>
                  </div>
                  <Textarea value={pdde} readOnly rows={2} className="text-xs" data-testid="pdde-term-text" />
                </div>
              </div>

              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={() => setItemsForNfOpen(false)} data-testid="btn-items-nf-close">
                  Fechar
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        );
      })()}

      {/* Finalize dialog */}
      <Dialog open={finalizeOpen} onOpenChange={setFinalizeOpen}>
        <DialogContent className="max-w-md" data-testid="finalize-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCheck className="w-5 h-5 text-emerald-600" />
              Finalizar orçamento Nº {budget.number}
            </DialogTitle>
            <DialogDescription>
              Todos os itens estão concluídos. Deseja enviar um WhatsApp ao diretor avisando a finalização e solicitando autorização para emitir a nota fiscal?
            </DialogDescription>
          </DialogHeader>
          <div className="bg-slate-50 rounded-lg p-3 text-xs text-slate-700 border border-slate-100">
            <p className="font-semibold mb-1">Prévia da mensagem:</p>
            <p className="whitespace-pre-wrap">
              {`Olá ${budget.school?.director_name || "Diretor(a)"}! Concluímos os serviços referentes ao orçamento Nº ${budget.number} da ${budget.school?.name || "escola"}. Podemos emitir a nota fiscal? Obrigado! — AMC Escolas`}
            </p>
            {!(budget.school?.whatsapp_director || budget.school?.phone) && (
              <p className="text-rose-600 mt-2 font-semibold">⚠ Diretor sem WhatsApp/Telefone cadastrado.</p>
            )}
          </div>
          <DialogFooter className="gap-2 flex-col sm:flex-row">
            <Button variant="outline" onClick={() => setFinalizeOpen(false)} disabled={finalizing} data-testid="btn-finalize-cancel">
              Cancelar
            </Button>
            <Button
              variant="outline"
              onClick={() => doFinalize(false)}
              disabled={finalizing}
              data-testid="btn-finalize-no-whatsapp"
            >
              Finalizar sem enviar
            </Button>
            <Button
              onClick={() => doFinalize(true)}
              disabled={finalizing}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="btn-finalize-with-whatsapp"
            >
              <Send className="w-4 h-4 mr-2" />
              Finalizar e enviar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Fiscal data dialog */}
      <Dialog open={fiscalOpen} onOpenChange={setFiscalOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="fiscal-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="w-5 h-5 text-[#7c3aed]" />
              Gravar dados da nota fiscal
            </DialogTitle>
            <DialogDescription>
              Nota emitida manualmente por fora do sistema. Preencha os dados abaixo para registrar.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            {/* School header */}
            <div className="bg-slate-50 rounded-lg p-3 border border-slate-100 text-xs text-slate-700 space-y-0.5">
              <p><strong>Escola:</strong> {budget.school?.name || "—"}</p>
              {budget.school?.cnpj && <p><strong>CNPJ:</strong> {budget.school.cnpj}</p>}
              {budget.school?.address && <p><strong>Endereço:</strong> {budget.school.address}</p>}
              {budget.school?.director_name && <p><strong>Diretor(a):</strong> {budget.school.director_name}</p>}
              <p className="pt-1"><strong>Valor total do orçamento:</strong> <span className="font-bold text-slate-900">{BRL(budget.total)}</span></p>
            </div>

            <div>
              <Label className="text-xs font-semibold">Número da nota fiscal *</Label>
              <Input
                value={fiscalForm.nf_number}
                onChange={(e) => setFiscalForm((f) => ({ ...f, nf_number: e.target.value }))}
                placeholder="Nº da nota"
                data-testid="input-nf-number"
                className="mt-1"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold">ISS {taxPct.iss}% (R$)</Label>
                <MoneyInput
                  value={fiscalForm.nf_iss_value}
                  onChange={(v) => setFiscalForm((f) => ({ ...f, nf_iss_value: v }))}
                  data-testid="input-nf-iss"
                  className="mt-1"
                />
                <p className="text-[10px] text-slate-500 mt-1">Pré-calculado ({taxPct.iss}%): {BRL((budget.total || 0) * taxPct.iss / 100)}</p>
              </div>
              <div>
                <Label className="text-xs font-semibold">Simples {taxPct.simples}% (R$)</Label>
                <MoneyInput
                  value={fiscalForm.nf_simples_value}
                  onChange={(v) => setFiscalForm((f) => ({ ...f, nf_simples_value: v }))}
                  data-testid="input-nf-simples"
                  className="mt-1"
                />
                <p className="text-[10px] text-slate-500 mt-1">Pré-calculado ({taxPct.simples}%): {BRL((budget.total || 0) * taxPct.simples / 100)}</p>
              </div>
            </div>

            <div>
              <Label className="text-xs font-semibold">Anexar PDF da nota fiscal</Label>
              <div className="mt-1 flex items-center gap-2">
                <label className="flex-1 border-2 border-dashed border-emerald-300 rounded-lg p-3 text-center cursor-pointer hover:bg-emerald-50 transition-colors">
                  <input
                    type="file"
                    accept="application/pdf,image/*"
                    className="hidden"
                    onChange={(e) => uploadFiscalFile(e.target.files?.[0])}
                    data-testid="input-nf-file"
                  />
                  <Upload className="w-4 h-4 mx-auto text-emerald-600" />
                  <p className="text-[11px] text-slate-600 mt-1">
                    {fiscalUploading ? "Enviando..." : (fiscalForm.nf_file_path ? "Trocar arquivo" : "Clique para carregar")}
                  </p>
                </label>
              </div>
              {fiscalForm.nf_file_path && (
                <p className="text-[11px] text-emerald-700 mt-1 truncate" data-testid="nf-file-attached">
                  ✓ Arquivo anexado
                </p>
              )}
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setFiscalOpen(false)} disabled={fiscalSaving} data-testid="btn-fiscal-cancel">
              Cancelar
            </Button>
            <Button
              onClick={saveFiscalData}
              disabled={fiscalSaving || fiscalUploading}
              className="bg-[#7c3aed] hover:bg-[#6d28d9] text-white"
              data-testid="btn-fiscal-save"
            >
              {fiscalSaving ? "Salvando..." : "Gravar dados!"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <MergeBudgetDialog open={mergeOpen} onOpenChange={setMergeOpen} budget={budget} />

      {/* Split dialog */}
      <Dialog open={splitOpen} onOpenChange={setSplitOpen}>
        <DialogContent className="max-w-lg" data-testid="split-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <GitBranch className="w-5 h-5 text-amber-600" />
              Desmembrar orçamento
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-slate-600">
              Selecione os itens que serão <strong>movidos para um novo orçamento</strong>. O orçamento atual ficará apenas com os itens não selecionados.
            </p>
            {budget?.items?.length > 0 ? (
              <ul className="divide-y divide-slate-100 max-h-80 overflow-y-auto border border-slate-100 rounded-xl">
                {budget.items.map((it, idx) => (
                  <li key={it.id} className="flex items-start gap-3 p-3 hover:bg-slate-50">
                    <Checkbox
                      checked={!!splitSel[it.id]}
                      onCheckedChange={() => toggleSplitItem(it.id)}
                      data-testid={`split-item-${idx}`}
                      className="mt-0.5"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-slate-900 line-clamp-2">{it.description || "(sem descrição)"}</p>
                      <p className="text-[11px] text-slate-500 mt-0.5">
                        Qtd {it.quantity || 1} × {BRL(it.unit_value || 0)} = <strong>{BRL(it.total || 0)}</strong>
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-xs text-slate-500 text-center py-4">Sem itens para desmembrar.</p>
            )}
            <div className="flex items-center justify-between text-xs">
              <span className="text-slate-500">{splitCount} item(ns) selecionado(s)</span>
              <span className="text-slate-500">Novo orçamento virá como <strong className="text-slate-700">aguardando</strong></span>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setSplitOpen(false)}>Cancelar</Button>
            <Button
              onClick={confirmSplit}
              disabled={splitting || splitCount === 0}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="btn-confirm-split"
            >
              <GitBranch className="w-4 h-4 mr-2" />
              {splitting ? "Processando..." : `Desmembrar ${splitCount || ""}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Share WhatsApp dialog (reusable) */}
      {budget && budget.school && (
        <ShareBudgetDialog
          open={shareDialog.open}
          onOpenChange={(o) => setShareDialog((s) => ({ ...s, open: o }))}
          budget={budget}
          school={budget.school}
          title={shareDialog.title}
          headlineLabel={shareDialog.headlineLabel}
          extraMessage={shareDialog.extraMessage}
        />
      )}

      {/* General photos manager */}
      {budget && (
        <GeneralPhotosDialog
          open={generalPhotosOpen}
          onClose={() => setGeneralPhotosOpen(false)}
          budget={budget}
          onUpdate={(photos) => setBudget((prev) => prev && ({ ...prev, general_photos: photos }))}
        />
      )}

      {/* Finance result modal (opens after saving NF data) */}
      <FinanceResultDialog
        open={financeOpen}
        onOpenChange={setFinanceOpen}
        budgetId={id}
      />

      {/* Live chat admin ↔ escola */}
      {!isCompetitor && budget.school_id && (
        <div id="budget-chat-anchor" className="scroll-mt-24">
          <BudgetChatPanel budgetId={id} role="admin" />
        </div>
      )}

      {/* Ad-hoc message to the school */}
      <Dialog open={msgDialog} onOpenChange={setMsgDialog}>
        <DialogContent className="max-w-md" data-testid="msg-school-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading font-black text-[#1e3a8a]">Mensagem pra escola</DialogTitle>
            <DialogDescription>
              Essa mensagem aparece no sininho da escola e vai como push (quando ela tem o app instalado). Use pra avisar algo pontual que não está ligado a mudar status ou item.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={msgText}
            onChange={(e) => setMsgText(e.target.value)}
            rows={5}
            maxLength={2000}
            placeholder="Ex.: Diretora, passo aí amanhã às 14h pra ver o quadro da sala 3. Qualquer coisa me avisa por aqui."
            data-testid="msg-school-input"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setMsgDialog(false)}>Cancelar</Button>
            <Button
              disabled={sendingMsg || !msgText.trim()}
              onClick={async () => {
                setSendingMsg(true);
                try {
                  // Mensagem explícita → sempre envia, mesmo se o toggle
                  // está silenciado.  Fazemos isso bypassando o header
                  // temporariamente.
                  await api.post(`/budgets/${id}/message-school`, { text: msgText.trim() }, { headers: { "X-Notify-School": "1" } });
                  toast.success("Mensagem enviada pra escola");
                  setMsgDialog(false);
                } catch (err) {
                  toast.error(err?.response?.data?.detail || "Falha ao enviar");
                } finally {
                  setSendingMsg(false);
                }
              }}
              className="bg-sky-600 hover:bg-sky-700 text-white"
              data-testid="btn-send-admin-message-confirm"
            >
              <Send className="w-4 h-4 mr-1" />
              {sendingMsg ? "Enviando..." : "Enviar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
